import Cancel from "@mui/icons-material/Cancel";
import CheckCircle from "@mui/icons-material/CheckCircle";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { green, red, orange } from "@mui/material/colors";
import { CredentialVerificationStatusChip } from "components/CredentialVerificationStatusChip";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  CredentialImplementationsForStaffQuery,
  ImplementationStatus,
  CredentialVerificationStatus,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { credentialVerificationStatusToLabel } from "util/constants";
import { prettyPrintDateTimeMed } from "util/dates";

import { ImplementationStatusChip } from "./Drawer/ImplementationStatusChip";

export type Row =
  CredentialImplementationsForStaffQuery["credentialImplementationsForStaff"]["items"][number];

const CREDENTIAL_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(CredentialVerificationStatus).map((status) => [
    status,
    credentialVerificationStatusToLabel(status),
  ])
);

function BooleanIcon({ value }: { value: boolean }) {
  return value ? (
    <CheckCircle sx={{ color: green[600], fontSize: 20 }} />
  ) : (
    <Cancel sx={{ color: red[300], fontSize: 20 }} />
  );
}

export const COLUMNS: OmnisearchGridColDef<Row>[] = [
  {
    // `field: "tenant"` is the omnisearch key (server expects `tenantId:<uuid>|...`
    // via `MultipleTenantPicker` -> `FilterPopover`); the column displays
    // `row.tenantName`. Mirrors the pattern in `Staff/Notifications.tsx`.
    field: "tenant",
    headerName: "Organization",
    width: 180,
    filterKeyType: "multipleTenant",
    valueGetter: (_, row) => row.tenantName,
  },
  {
    field: "state",
    headerName: "State/Territory",
    width: 80,
    filterKeyType: "enumAutocomplete",
    enumValues: Object.keys(STATE_NAMES),
    enumPresentationFunction: (value) => STATE_NAMES[value as keyof typeof STATE_NAMES] ?? value,
  },
  {
    field: "portalName",
    headerName: "Portal",
    width: 160,
  },
  {
    // `field: "facility"` is the omnisearch key (`MultipleFacilityPicker` ->
    // `facilityId:<uuid>|...`). Display value comes from `row.facilityName`.
    field: "facility",
    headerName: "Facility",
    width: 220,
    filterKeyType: "multipleFacility",
    valueGetter: (_, row) => row.facilityName,
    renderCell: ({ row }) => (
      <Tooltip title={row.facilityAddress ?? ""}>
        <Typography variant="body2" noWrap>
          {row.facilityName ?? "—"}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "county",
    headerName: "County",
    width: 120,
  },
  {
    field: "credentialName",
    headerName: "Credential Name",
    width: 160,
  },
  {
    field: "credentialEmail",
    headerName: "Credential Email",
    width: 180,
  },
  {
    field: "credentialInEncamp",
    headerName: "Credential in Encamp?",
    width: 160,
    align: "center",
    headerAlign: "center",
    filterKeyType: "boolean",
    renderCell: ({ row }) => <BooleanIcon value={row.credentialInEncamp} />,
  },
  {
    field: "implementationStatus",
    headerName: "Implementation Status",
    width: 180,
    filterKeyType: "enum",
    enumValues: Object.values(ImplementationStatus),
    enumPresentationFunction: prettyPrintEnumValue,
    renderCell: ({ row }) => <ImplementationStatusChip status={row.implementationStatus} />,
  },
  {
    field: "credentialStatus",
    headerName: "Credential Status",
    width: 180,
    filterKeyType: "enum",
    enumValues: Object.values(CredentialVerificationStatus),
    enumPresentationFunction: (value) => CREDENTIAL_STATUS_LABELS[value] ?? value,
    renderCell: ({ row }) => (
      <CredentialVerificationStatusChip status={row.credentialStatus} emptyFallback="—" />
    ),
  },
  {
    field: "lastVerifiedAt",
    headerName: "Last Verified",
    width: 160,
    filterKeyType: "date",
    renderCell: ({ row }) =>
      row.lastVerifiedAt ? prettyPrintDateTimeMed(row.lastVerifiedAt) : "—",
  },
  {
    field: "lockHolder",
    headerName: "Currently in Use",
    width: 130,
    align: "center",
    headerAlign: "center",
    filterKeyType: "boolean",
    renderCell: ({ row }) => {
      if (!row.lockHolder) return "";
      const expiry = row.lockExpiresAt ? prettyPrintDateTimeMed(row.lockExpiresAt) : "";
      const tooltipText = expiry ? `${row.lockHolder} (expires ${expiry})` : row.lockHolder;
      return (
        <Tooltip title={tooltipText}>
          <LockOutlined sx={{ color: orange[700], fontSize: 20 }} />
        </Tooltip>
      );
    },
  },
  {
    field: "seenInPortal",
    headerName: "Facility in Portal",
    description:
      "Indicates whether this facility's state ID was seen in the portal during the last credential verification check",
    width: 140,
    align: "center",
    headerAlign: "center",
    filterKeyType: "boolean",
    renderHeader: () => (
      <Tooltip
        title="Indicates whether this facility's state ID was seen in the portal during the last credential verification check"
        placement="top"
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {/* Match the default MuiDataGrid-columnHeaderTitle weight so this
              header doesn't read as lighter than the others. */}
          <Box component="span" sx={{ fontWeight: 500 }}>
            Facility in Portal
          </Box>
          <InfoOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
        </Stack>
      </Tooltip>
    ),
    renderCell: ({ row }) => <BooleanIcon value={row.seenInPortal} />,
  },
  {
    field: "stateId",
    headerName: "State ID in Encamp",
    width: 160,
    renderCell: ({ row }) => {
      if (!row.stateId) return <Typography color="text.secondary">Missing</Typography>;
      return (
        <Tooltip title={row.stateIdType ?? ""}>
          <Typography variant="body2" noWrap>
            {row.stateId}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    // Plain text filter — `WorkflowActivitiesTable` / `ChemicalFacilitiesDataGrid`
    // use the same pattern for CSM/AM/etc. Server runs an `ILIKE` against the
    // active TenantContact (type=CSM) `firstName`/`lastName`/`email`.
    field: "csmName",
    headerName: "CSM",
    width: 140,
    renderCell: ({ row }) => {
      if (!row.csmName) return "—";
      return (
        <Tooltip title={row.csmEmail ?? ""}>
          <Typography variant="body2" noWrap>
            {row.csmName}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    // Plain text filter on assignee email + "Unassigned only" null checkbox —
    // mirrors `WorkflowActivitiesTable`'s assignee column. We avoid
    // `MultipleUserPicker` here because on `/staff` routes the underlying
    // tenant `users` query has no `currentTenantId` and 403s.
    field: "assigneeEmail",
    headerName: "Assignee",
    width: 140,
    nullCheckboxLabel: "Unassigned only",
    valueGetter: (_, row) => row.assigneeName,
    renderCell: ({ row }) => {
      if (!row.assigneeName) return "—";
      return (
        <Tooltip title={row.assigneeEmail ?? ""}>
          <Typography variant="body2" noWrap>
            {row.assigneeName}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "mostRecentComment",
    headerName: "Most Recent Comment",
    width: 200,
    sortable: false,
    renderCell: ({ row }) => {
      if (!row.mostRecentComment) return "";
      const dateStr = row.mostRecentCommentAt
        ? prettyPrintDateTimeMed(row.mostRecentCommentAt)
        : "";
      return (
        <Tooltip title={`${dateStr}: ${row.mostRecentComment}`}>
          <Typography variant="body2" noWrap>
            {row.mostRecentComment}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "mostRecentCommentAt",
    headerName: "Last Comment Date",
    width: 160,
    sortable: true,
    filterable: false,
    renderCell: ({ row }) =>
      row.mostRecentCommentAt ? prettyPrintDateTimeMed(row.mostRecentCommentAt) : "—",
  },
  {
    field: "notes",
    headerName: "Notes",
    width: 180,
    sortable: false,
    renderCell: ({ row }) => {
      const text = row.blockedReason ? `[Blocked] ${row.blockedReason}` : row.notes;
      if (!text) return "";
      return (
        <Tooltip title={text}>
          <Typography variant="body2" noWrap>
            {text}
          </Typography>
        </Tooltip>
      );
    },
  },
];
